import { useEffect, useState } from "react";

import type { CarryOverSource, PersonId } from "@thalamos/haven-common";

import api from "../../api/index.js";
import type { PreviousAssessment } from "../../components/StartAssessmentDialog/StartAssessmentDialog.js";

/**
 * The finished assessment a start would follow, as the start dialog describes
 * it: whose it was is told only where it was somebody else's.
 */
export const previousForStart = (
  source: CarryOverSource,
  viewerUserId: string | undefined,
): PreviousAssessment => ({
  instrument: source.instrument.name,
  status: source.status,
  finishedAt: source.finishedAt,
  assessorName:
    source.assessorUserId === viewerUserId ? undefined : source.assessorName,
  approverName: source.approverName ?? undefined,
});

/** Reading the offer: still under way, or settled with what it offered, if anything. */
export type CarryOverOfferReading =
  { settled: false } | { settled: true; source: CarryOverSource | undefined };

/** A settled reading, and whose record it was read for. */
type Settled = {
  personId: PersonId;
  source: CarryOverSource | undefined;
};

/**
 * What starting an assessment about a young person now would be offered to
 * carry over, read only while `enabled`; it stays unsettled until then.
 *
 * Settles with nothing where the read fails: the start goes on without
 * carrying anything over rather than being held up by it.
 */
export const useCarryOverOffer = (
  personId: PersonId | undefined,
  enabled: boolean,
): CarryOverOfferReading => {
  const [settled, setSettled] = useState<Settled>();

  useEffect(() => {
    if (personId === undefined || !enabled) {
      return;
    }

    let current = true;

    void api.assessment
      .carryOverOffer(personId)
      .then((answer) => answer.data?.source ?? undefined)
      .catch(() => undefined)
      .then((source) => {
        if (current) {
          setSettled({ personId, source });
        }
      });

    return () => {
      current = false;
    };
  }, [personId, enabled]);

  // Read for another record, so nothing yet about this one.
  return settled === undefined || settled.personId !== personId
    ? { settled: false }
    : { settled: true, source: settled.source };
};
